import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import {
  ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import { useDestrierTheme, getCardSx } from '../ThemeContext';
import { useDataCache } from '../DataCacheContext';
import { API_BASE } from '../api';
import TimePeriodFilter from './TimePeriodFilter';

const ACCENT = '#8A74E4';
const PIECE_COLORS = { knight: '#E4446E', bishop: '#00A5C4', rook: '#8F7BE0', queen: '#D66DD3', pawn: '#DE7345', king: '#8b95a8' };
const PIECE_SYMBOLS = { knight: '\u265E', bishop: '\u265D', rook: '\u265C', queen: '\u265B', pawn: '\u265F', king: '\u265A' };
const TERM_ADVICE = { timeout: 'Work on clock management', resignation: "You're giving up too easily \u2014 fight harder", checkmate: 'Great \u2014 you play to the end' };


function formatTime(seconds) {
  if (seconds >= 60) return `${(seconds / 60).toFixed(1)}m`;
  return `${seconds.toFixed(1)}s`;
}

function CurveTooltip({ active, payload, c }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <Box sx={{ background: c.bg, border: `1px solid ${c.textMuted}25`, borderRadius: '12px', p: 2, boxShadow: `0 4px 16px ${c.textMuted}15` }}>
      <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mb: 0.5 }}>Move {d.move}</Typography>
      <Typography sx={{ color: '#8A74E4', fontSize: '1.05rem', fontWeight: 700 }}>{d.avg_time}s — Your time</Typography>
      {d.fast_trend != null && <Typography sx={{ color: '#00A5C4', fontSize: '0.92rem', fontWeight: 600, mt: 0.3 }}>{d.fast_trend.toFixed(1)}s — Fast avg</Typography>}
      {d.slow_trend != null && <Typography sx={{ color: '#E4446E', fontSize: '0.92rem', fontWeight: 600, mt: 0.3 }}>{d.slow_trend.toFixed(1)}s — Slow avg</Typography>}
    </Box>
  );
}

export default function PlayHabitsPage() {
  const { c } = useDestrierTheme();
  const cardSx = getCardSx(c);
  const cache = useDataCache();
  const [searchParams] = useSearchParams();
  const username = searchParams.get('username');
  const [period, setPeriod] = useState('all');
  const [timeData, setTimeData] = useState(null);
  const [styleData, setStyleData] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [phase, setPhase] = useState('fetching');
  const [termTab, setTermTab] = useState('all');
  const [chartGameType, setChartGameType] = useState('all');

  useEffect(() => {
    if (!username) return;
    const tkc = `time:${username}:${period}`;
    const skc = `style:${username}:${period}`;
    const rkc = `free-report:${username}:${period}`;
    const hitTime = cache.get(tkc);
    const hitStyle = cache.get(skc);
    const hitReport = cache.get(rkc);
    if (hitTime) setTimeData(hitTime);
    if (hitStyle) setStyleData(hitStyle);
    if (hitReport) setReportData(hitReport);
    let pending = 0;
    let cancelled = false;
    const checkDone = () => { pending--; if (!cancelled && pending <= 0) setPhase('ready'); };
    if (!hitTime) { pending++; fetch(`${API_BASE}/time-management?username=${username}&period=${period}`).then(r => r.ok ? r.json() : Promise.reject()).then(d => { if (!cancelled) { cache.set(tkc, d); setTimeData(d); } checkDone(); }).catch(checkDone); }
    if (!hitStyle) { pending++; fetch(`${API_BASE}/play-style?username=${username}&period=${period}`).then(r => r.ok ? r.json() : Promise.reject()).then(d => { if (!cancelled) { cache.set(skc, d); setStyleData(d); } checkDone(); }).catch(checkDone); }
    if (!hitReport) { pending++; fetch(`${API_BASE}/free-report?username=${username}&period=${period}`).then(r => r.ok ? r.json() : Promise.reject()).then(d => { if (!cancelled) { cache.set(rkc, d); setReportData(d); } checkDone(); }).catch(checkDone); }
    if (pending === 0) setPhase('ready');
    else setPhase('fetching');
    return () => { cancelled = true; };
  }, [username, period]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!username) {
    return <Box sx={{ minHeight: '100vh', background: c.pageBg || c.bg, display: 'flex', justifyContent: 'center', alignItems: 'center' }}><Typography sx={{ color: c.textMuted }}>Please provide a username.</Typography></Box>;
  }

  // Radar values come from piece_usage (% of moves per piece), rescaled so the
  // most-used piece maps to 1.0. Pawns dominate — exclude them so the radar
  // differentiates the minor/major piece balance meaningfully.
  const pieceUsage = styleData?.piece_usage || null;
  const radarData = (() => {
    if (!pieceUsage) return [];
    const entries = Object.entries(pieceUsage).filter(([p]) => p !== 'pawn');
    const maxPct = Math.max(...entries.map(([, v]) => v?.pct || 0), 1);
    return entries.map(([piece, v]) => ({
      axis: piece.charAt(0).toUpperCase() + piece.slice(1),
      value: (v?.pct || 0) / maxPct,
      piece,
      pct: v?.pct || 0,
    }));
  })();
  const strongestPiece = radarData.length > 0 ? radarData.reduce((a, b) => a.value > b.value ? a : b) : null;
  // best_hour / best_day come from /free-report → best_time, NOT /time-management.
  // The API returns {hour, games, win_rate} / {day, games, win_rate} objects.
  const bestHour = reportData?.best_time?.best_hour?.hour;
  const bestDay = reportData?.best_time?.best_day?.day;
  const worstHour = reportData?.best_time?.worst_hour?.hour;
  const worstDay = reportData?.best_time?.worst_day?.day;
  const byDayHour = reportData?.best_time?.by_day_hour || [];
  const termRaw = reportData?.termination;
  const termSource = termTab === 'win' ? termRaw?.how_you_win : termTab === 'lose' ? termRaw?.how_you_lose : termRaw?.breakdown;
  const termBreakdown = termSource ? Object.entries(termSource).map(([type, data]) => ({ type, count: data.count, pct: Math.round(data.pct * 100) })).sort((a, b) => b.count - a.count) : null;
  const TERM_COLORS = ['#00A5C4', '#E4446E', '#8F7BE0', '#DE7345', '#D66DD3', '#8b95a8'];

  return (
    <Box sx={{ minHeight: '100vh', background: c.pageBg || c.bg }}>
      {phase === 'fetching' && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', border: `3px solid ${c.accent}20`, borderTopColor: c.accent, animation: 'spin 0.8s linear infinite', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />
        </Box>
      )}

      {phase === 'ready' && (
        <Box sx={{ px: { xs: 2, md: 3, lg: 4 }, pb: 6, pt: { xs: 9, md: 10 } }}>
          <Box sx={{ position: 'fixed', top: { xs: 56, md: 72 }, left: { xs: 0, md: 0 }, right: { xs: 0, md: '35px' }, display: 'flex', justifyContent: 'center', zIndex: 40, py: 1, pointerEvents: 'none' }}>
            <Box sx={{ pointerEvents: 'auto' }}>
              <TimePeriodFilter value={period} onChange={setPeriod} />
            </Box>
          </Box>

          {/* Page Header */}
          <Box sx={{ mb: 5 }}>
            <Typography sx={{ color: c.textDark, fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.6rem' }, fontWeight: 900, letterSpacing: '-0.03em', mb: 1 }}>
              Play Habits
            </Typography>
            <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7 }}>
              Beyond wins and losses, this page reveals <em>how</em> you play — your clock patterns,
              piece preferences, emotional tendencies, and how your games end.
              These are the habits that shape your results. Understanding them is the first step to changing them.
            </Typography>
          </Box>

          {/* ── Piece Mastery ── */}
          {radarData.length > 0 && (() => {
            const totalGames = styleData?.total_games || 0;
            const movesPerGame = (moves) => totalGames > 0 ? (moves / totalGames).toFixed(1) : '—';
            const pu = styleData?.piece_usage || {};
            const castling = styleData?.castling || {};
            // Knight vs bishop preference ratio
            const nMoves = pu.knight?.moves || 0;
            const bMoves = pu.bishop?.moves || 0;
            const piecePrefLabel = nMoves >= bMoves
              ? { label: 'Knights', ratio: bMoves > 0 ? (nMoves / bMoves).toFixed(2) : nMoves > 0 ? '∞' : '—', vs: 'bishops', color: PIECE_COLORS.knight }
              : { label: 'Bishops', ratio: nMoves > 0 ? (bMoves / nMoves).toFixed(2) : '∞', vs: 'knights', color: PIECE_COLORS.bishop };
            return (
            <Box sx={{ mb: 5 }}>
              <Typography sx={{ color: c.textDark, fontSize: { xs: '1.4rem', md: '1.6rem' }, fontWeight: 800, mb: 0.5 }}>Piece Mastery</Typography>
              <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mb: 3 }}>
                Which pieces carry your play. Pawns are excluded so the minor/major-piece balance shows clearly.
                Values are <strong>relative to your most-used piece</strong>: your favorite sits at 100%, and a piece at 50% means you move it half as often.
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'stretch' }}>
                {/* Radar — square, pushed left, taller */}
                <Box sx={{ ...cardSx, p: 3, width: { xs: '100%', md: 440 }, aspectRatio: { md: '1 / 1' }, height: { xs: 340, md: 'auto' }, flexShrink: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="72%">
                      <PolarGrid stroke={`${c.textMuted}20`} />
                      <PolarAngleAxis dataKey="axis" tick={({ x, y, payload }) => {
                        const sym = PIECE_SYMBOLS[payload.value.toLowerCase()] || '';
                        const cellPct = radarData.find(r => r.axis === payload.value)?.pct;
                        return (
                          <g>
                            <text x={x} y={y - 6} fill={PIECE_COLORS[payload.value.toLowerCase()] || c.textMuted} fontSize={13} fontWeight={700} textAnchor="middle" dominantBaseline="central">{sym} {payload.value}</text>
                            {cellPct != null && <text x={x} y={y + 10} fill={c.textMuted} fontSize={11} fontWeight={500} textAnchor="middle" dominantBaseline="central">{cellPct}% of moves</text>}
                          </g>
                        );
                      }} />
                      <PolarRadiusAxis
                        angle={90}
                        domain={[0, 1]}
                        ticks={[0.25, 0.5, 0.75, 1]}
                        tick={{ fill: c.textMuted, fontSize: 10 }}
                        tickFormatter={(v) => `${Math.round(v * 100)}%`}
                        axisLine={false}
                        stroke={`${c.textMuted}40`}
                      />
                      <Radar dataKey="value" stroke={ACCENT} fill={ACCENT} fillOpacity={0.2} strokeWidth={2.5} dot={{ r: 3.5, fill: ACCENT }} />
                    </RadarChart>
                  </ResponsiveContainer>
                </Box>
                {/* Right: Strongest Piece only */}
                <Box sx={{ flex: 1, display: 'flex' }}>
                  {strongestPiece && (
                    <Box sx={{ ...cardSx, p: 3, flex: 1, border: `2px solid ${PIECE_COLORS[strongestPiece.piece]}25`, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1 }}>Strongest Piece</Typography>
                      <Typography sx={{ color: PIECE_COLORS[strongestPiece.piece], fontSize: '2.4rem', fontWeight: 900, lineHeight: 1.1 }}>
                        {PIECE_SYMBOLS[strongestPiece.piece]} {strongestPiece.axis}
                      </Typography>
                      <Typography sx={{ color: c.textDark, fontSize: '1.4rem', fontWeight: 800, mt: 0.5 }}>{strongestPiece.pct}% of moves</Typography>
                      <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mt: 1.5, lineHeight: 1.6 }}>
                        This piece carries your play. Look for positions where it dominates — e.g. knights love closed positions, bishops love open diagonals.
                      </Typography>
                      <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mt: 1, lineHeight: 1.6, fontStyle: 'italic' }}>
                        Practice {strongestPiece.axis.toLowerCase()} tactics on Lichess or Chess.com puzzles filtered by this piece.
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Piece usage stats — row of stat cards */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mt: 3 }}>
                {/* Minor-piece preference */}
                <Box sx={{ ...cardSx, p: 2.5, textAlign: 'center' }}>
                  <Typography sx={{ color: c.textMuted, fontSize: '1rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.8 }}>Minor-Piece Pick</Typography>
                  <Typography sx={{ color: piecePrefLabel.color, fontSize: '1.5rem', fontWeight: 900, lineHeight: 1.1 }}>{piecePrefLabel.ratio}×</Typography>
                  <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 600, mt: 0.3 }}>
                    more {piecePrefLabel.label.toLowerCase()} than {piecePrefLabel.vs}
                  </Typography>
                </Box>
                {/* Castling profile */}
                {castling.total > 0 && (
                  <Box sx={{ ...cardSx, p: 2.5, textAlign: 'center' }}>
                    <Typography sx={{ color: c.textMuted, fontSize: '1rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.8 }}>Castling</Typography>
                    <Typography sx={{ color: '#00A5C4', fontSize: '1.5rem', fontWeight: 900, lineHeight: 1.1 }}>
                      {Math.round(castling.kingside_pct)}% short
                    </Typography>
                    <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 600, mt: 0.3 }}>
                      castled in {Math.round(castling.castle_rate)}% of games
                    </Typography>
                  </Box>
                )}
                {/* Queen activity */}
                {pu.queen && (
                  <Box sx={{ ...cardSx, p: 2.5, textAlign: 'center' }}>
                    <Typography sx={{ color: c.textMuted, fontSize: '1rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.8 }}>{PIECE_SYMBOLS.queen} Queen Sorties</Typography>
                    <Typography sx={{ color: PIECE_COLORS.queen, fontSize: '1.5rem', fontWeight: 900, lineHeight: 1.1 }}>
                      {movesPerGame(pu.queen.moves)}
                    </Typography>
                    <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 600, mt: 0.3 }}>
                      queen moves per game
                    </Typography>
                  </Box>
                )}
                {/* Pawn pushes */}
                {pu.pawn && (
                  <Box sx={{ ...cardSx, p: 2.5, textAlign: 'center' }}>
                    <Typography sx={{ color: c.textMuted, fontSize: '1rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.8 }}>{PIECE_SYMBOLS.pawn} Pawn Pushes</Typography>
                    <Typography sx={{ color: PIECE_COLORS.pawn, fontSize: '1.5rem', fontWeight: 900, lineHeight: 1.1 }}>
                      {movesPerGame(pu.pawn.moves)}
                    </Typography>
                    <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 600, mt: 0.3 }}>
                      pawn moves per game
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Per-piece breakdown table */}
              {Object.keys(pu).length > 0 && (
                <Box sx={{ ...cardSx, p: 3, mt: 2 }}>
                  <Typography sx={{ color: c.textMuted, fontSize: '1rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', mb: 2 }}>
                    Moves per piece, across {totalGames.toLocaleString()} games
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                    {['queen', 'rook', 'bishop', 'knight', 'pawn', 'king']
                      .filter(p => pu[p])
                      .sort((a, b) => (pu[b].moves || 0) - (pu[a].moves || 0))
                      .map(p => {
                        const moves = pu[p].moves || 0;
                        const pct = pu[p].pct || 0;
                        const maxPct = Math.max(...Object.values(pu).map(v => v?.pct || 0), 1);
                        return (
                          <Box key={p} sx={{ display: 'grid', gridTemplateColumns: { xs: '80px 1fr 60px', sm: '100px 1fr 80px 80px' }, gap: 1.5, alignItems: 'center' }}>
                            <Typography sx={{ color: PIECE_COLORS[p], fontSize: '1.1rem', fontWeight: 700, textTransform: 'capitalize' }}>
                              {PIECE_SYMBOLS[p]} {p}
                            </Typography>
                            <Box sx={{ height: 8, borderRadius: 4, bgcolor: `${c.textMuted}10`, overflow: 'hidden', position: 'relative' }}>
                              <Box sx={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${(pct / maxPct) * 100}%`, bgcolor: PIECE_COLORS[p], borderRadius: 4, transition: 'width 0.6s ease' }} />
                            </Box>
                            <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 700, textAlign: 'right' }}>{pct}%</Typography>
                            <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>{moves.toLocaleString()} moves</Typography>
                          </Box>
                        );
                      })}
                  </Box>
                </Box>
              )}
            </Box>
            );
          })()}

          {/* ── Clock Management ── */}
          {timeData?.time_curve && (() => {
            // Available game-type filters: only show types the player actually has data for
            const availableTypes = Object.keys(timeData.time_curves_by_type || {});
            const GAME_TYPE_ORDER = ['all', 'bullet', 'blitz', 'rapid', 'classical'];
            const tabs = GAME_TYPE_ORDER.filter(t => t === 'all' || availableTypes.includes(t));
            // Reference curve shape depends on time control: bullet peaks early+low, classical peaks later+high
            const CURVE_PARAMS = {
              all:       { peak: 16, decay: 12, fastMin: 1,   fastMax: 18,  slowMin: 5,   slowMax: 32 },
              bullet:    { peak: 6,  decay: 6,  fastMin: 0.3, fastMax: 1.5, slowMin: 0.8, slowMax: 3 },
              blitz:     { peak: 10, decay: 10, fastMin: 0.8, fastMax: 6,   slowMin: 2,   slowMax: 14 },
              rapid:     { peak: 18, decay: 14, fastMin: 1.5, fastMax: 22,  slowMin: 6,   slowMax: 45 },
              classical: { peak: 24, decay: 18, fastMin: 3,   fastMax: 55,  slowMin: 12,  slowMax: 110 },
            };
            const params = CURVE_PARAMS[chartGameType] || CURVE_PARAMS.all;
            const activeCurve = (chartGameType === 'all'
              ? timeData.time_curve
              : timeData.time_curves_by_type?.[chartGameType]) || [];
            return (
            <Box sx={{ mb: 5 }}>
              <Typography sx={{ color: c.textDark, fontSize: { xs: '1.4rem', md: '1.6rem' }, fontWeight: 800, mb: 0.5 }}>Clock Management</Typography>
              <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mb: 3 }}>
                How you spend your time throughout a game. The curve shows average seconds per move — spikes indicate where you think hardest.
                Ideally, you want to invest time in the middlegame (moves 10-25) and play the opening quickly from preparation.
              </Typography>
              {/* Game-type filter tabs */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ color: c.textMuted, fontSize: '0.95rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                  Game type
                </Typography>
                <Box sx={{ display: 'flex', bgcolor: `${c.textMuted}10`, borderRadius: '10px', p: 0.3 }}>
                  {tabs.map(key => (
                    <Box key={key} onClick={() => setChartGameType(key)}
                      sx={{
                        px: 1.5, py: 0.4, borderRadius: '8px', cursor: 'pointer',
                        fontSize: '1.05rem', fontWeight: chartGameType === key ? 700 : 500,
                        color: chartGameType === key ? c.textDark : c.textMuted,
                        bgcolor: chartGameType === key ? c.bg : 'transparent',
                        textTransform: 'capitalize', transition: 'all 0.2s ease',
                      }}
                    >
                      {key}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box sx={{ ...cardSx, p: { xs: 1.5, sm: 3 }, height: { xs: 320, sm: 380, md: 440 }, display: 'flex', flexDirection: 'column' }}>
                <Typography sx={{ fontSize: '1.05rem', color: c.textMuted, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 1 }}>
                  Time Per Move {chartGameType !== 'all' && <span style={{ textTransform: 'capitalize' }}>· {chartGameType}</span>}
                </Typography>
                {activeCurve.length === 0 ? (
                  <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ color: c.textMuted, fontSize: '1.05rem' }}>No {chartGameType} games in this period.</Typography>
                  </Box>
                ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={(() => {
                    return activeCurve.map((pt, i) => {
                      const moveNum = i + 1;
                      // Ease-out rise to peak, gaussian decay after — shape matches typical curve
                      const t = moveNum / params.peak;
                      const rise = t <= 1 ? 1 - Math.pow(1 - t, 2) : 1;
                      const decay = moveNum > params.peak ? Math.exp(-0.5 * Math.pow((moveNum - params.peak) / params.decay, 2)) : 1;
                      const shape = rise * decay;
                      return {
                        ...pt,
                        fast_trend: params.fastMin + shape * (params.fastMax - params.fastMin),
                        slow_trend: params.slowMin + shape * (params.slowMax - params.slowMin),
                      };
                    });
                  })()} margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
                    <defs>
                      <linearGradient id="timeGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={ACCENT} stopOpacity={0.25} />
                        <stop offset="100%" stopColor={ACCENT} stopOpacity={0.02} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={`${c.textMuted}12`} strokeDasharray="3 3" />
                    <XAxis dataKey="move" tick={{ fill: c.textMuted, fontSize: 11 }} tickLine={false} axisLine={{ stroke: `${c.textMuted}20` }} interval={0} />
                    <YAxis tick={{ fill: c.textMuted, fontSize: 11 }} tickLine={false} axisLine={{ stroke: `${c.textMuted}20` }} width={40} />
                    <Tooltip content={<CurveTooltip c={c} />} />
                    <Legend verticalAlign="bottom" height={28} wrapperStyle={{ fontSize: 15, fontWeight: 600, color: c.textMuted, paddingTop: 4 }} iconSize={14} />
                    <Area type="natural" dataKey="fast_trend" stroke="#00A5C4" strokeWidth={1.5} strokeDasharray="5 5" fill="none" dot={false} activeDot={false} name="Fast player avg" />
                    <Area type="monotone" dataKey="avg_time" stroke={ACCENT} strokeWidth={2} fill="url(#timeGrad)" dot={false} activeDot={{ r: 5, stroke: c.bg, strokeWidth: 2 }} name="Your time" />
                    <Area type="natural" dataKey="slow_trend" stroke="#E4446E" strokeWidth={1.5} strokeDasharray="5 5" fill="none" dot={false} activeDot={false} name="Slow player avg" />
                  </AreaChart>
                </ResponsiveContainer>
                )}
              </Box>
            </Box>
            );
          })()}

          {/* ── Time Trouble (solo card) ── */}
          {timeData?.time_trouble && (
            <Box sx={{ ...cardSx, p: 3, mb: 5 }}>
              <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1 }}>Time Trouble</Typography>
              <Typography sx={{ color: timeData.time_trouble.trouble_rate > 0.3 ? '#E4446E' : '#00A5C4', fontSize: '1.8rem', fontWeight: 900, lineHeight: 1.2 }}>
                {(timeData.time_trouble.trouble_rate * 100).toFixed(0)}%
              </Typography>
              <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mt: 1, lineHeight: 1.6 }}>
                of your games you enter time trouble ({timeData.time_trouble.games_in_trouble} games).
                {timeData.time_trouble.trouble_rate > 0.3
                  ? ' This is high. You\'re losing games to the clock, not the position. Practice making faster decisions in the opening and early middlegame.'
                  : ' This is under control. You manage your clock well — keep it up.'}
              </Typography>
            </Box>
          )}

          {/* ── Phase Breakdown ── */}
          {timeData?.phases && (
            <Box sx={{ mb: 5 }}>
              <Typography sx={{ color: c.textDark, fontSize: { xs: '1.4rem', md: '1.6rem' }, fontWeight: 800, mb: 0.5 }}>Think Time by Phase</Typography>
              <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mb: 3 }}>
                Where do you spend your clock? Opening moves should be quick (you know the theory). Middlegame is where deep calculation pays off. Endgame time depends on complexity.
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                {['opening', 'middlegame', 'endgame'].filter(p => timeData.phases[p]).map((p) => {
                  const phaseColors = { opening: '#00A5C4', middlegame: ACCENT, endgame: '#D66DD3' };
                  return (
                    <Box key={p} sx={{ ...cardSx, p: 3, textAlign: 'center' }}>
                      <Typography sx={{ color: phaseColors[p], fontSize: '1.05rem', fontWeight: 700, textTransform: 'capitalize', mb: 0.5 }}>{p}</Typography>
                      <Typography sx={{ color: c.textDark, fontSize: '1.8rem', fontWeight: 800 }}>{formatTime(timeData.phases[p].avg_time)}</Typography>
                      <Typography sx={{ color: c.textMuted, fontSize: '1.05rem' }}>{timeData.phases[p].total_moves.toLocaleString()} moves</Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* ── When You Play (day-of-week × hour heatmap + Golden Hour + Toughest window) ── */}
          {(bestDay || bestHour != null || worstDay || worstHour != null || byDayHour.length > 0) && (() => {
            const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
            const FULL_DAY = { Mon: 'Monday', Tue: 'Tuesday', Wed: 'Wednesday', Thu: 'Thursday', Fri: 'Friday', Sat: 'Saturday', Sun: 'Sunday' };
            const maxGames = Math.max(...byDayHour.map(b => b.games), 1);
            const cellMap = {};
            byDayHour.forEach(b => { cellMap[`${b.dow}-${b.hour}`] = b; });
            const goldenParts = [];
            if (bestDay) goldenParts.push(`${FULL_DAY[bestDay] || bestDay}s`);
            if (bestHour != null) goldenParts.push(`at ${bestHour}:00`);
            const goldenLabel = goldenParts.join(' ');
            const toughParts = [];
            if (worstDay) toughParts.push(`${FULL_DAY[worstDay] || worstDay}s`);
            if (worstHour != null) toughParts.push(`at ${worstHour}:00`);
            const toughLabel = toughParts.join(' ');
            return (
              <Box sx={{ mb: 5 }}>
                <Typography sx={{ color: c.textDark, fontSize: { xs: '1.4rem', md: '1.6rem' }, fontWeight: 800, mb: 0.5 }}>When You Play</Typography>
                <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mb: 3, maxWidth: 720 }}>
                  Some hours and days suit you better than others. Below, the heatmap shows your win rate across every day-of-week × hour slot — blue means you're winning there, red means you're losing. Use this to pick your moment for serious games.
                </Typography>

                {/* Golden Hour / Toughest Window cards */}
                {(goldenLabel || toughLabel) && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 3 }}>
                    {goldenLabel && (
                      <Box sx={{ ...cardSx, p: 3, borderLeft: `3px solid #00A5C4` }}>
                        <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1 }}>Golden Hour</Typography>
                        <Typography sx={{ color: '#00A5C4', fontSize: '1.8rem', fontWeight: 900, lineHeight: 1.2 }}>{goldenLabel}</Typography>
                        <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mt: 1, lineHeight: 1.6 }}>
                          Your best-performing window. Save your rated games for this slot when you can.
                        </Typography>
                      </Box>
                    )}
                    {toughLabel && (
                      <Box sx={{ ...cardSx, p: 3, borderLeft: `3px solid #E4446E` }}>
                        <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 1 }}>Toughest Window</Typography>
                        <Typography sx={{ color: '#E4446E', fontSize: '1.8rem', fontWeight: 900, lineHeight: 1.2 }}>{toughLabel}</Typography>
                        <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', mt: 1, lineHeight: 1.6 }}>
                          Your weakest window — likely tired, distracted, or rushing. Consider puzzles or analysis here instead.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Heatmap */}
                {byDayHour.length > 0 && (
                  <Box sx={{ ...cardSx, p: { xs: 2, sm: 3 }, overflowX: 'auto' }}>
                    <Typography sx={{ fontSize: '1.05rem', color: c.textMuted, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase', mb: 2 }}>
                      Win Rate · Day × Hour
                    </Typography>
                    <Box sx={{ minWidth: 600 }}>
                      <Box sx={{ display: 'grid', gridTemplateColumns: '36px repeat(24, 1fr)', gap: '3px', alignItems: 'center' }}>
                        {/* Header row: hour labels */}
                        <Box />
                        {Array.from({ length: 24 }, (_, h) => (
                          <Typography key={`hdr-${h}`} sx={{ fontSize: '0.7rem', color: c.textMuted, textAlign: 'center' }}>
                            {h % 4 === 0 ? h : ''}
                          </Typography>
                        ))}
                        {/* Day rows */}
                        {DAY_LABELS.map((day, dIdx) => (
                          <React.Fragment key={day}>
                            <Typography sx={{ fontSize: '0.8rem', color: c.textMuted, fontWeight: 600 }}>{day}</Typography>
                            {Array.from({ length: 24 }, (_, h) => {
                              const cell = cellMap[`${dIdx}-${h}`];
                              if (!cell) {
                                return <Box key={`${dIdx}-${h}`} sx={{ aspectRatio: '1', borderRadius: '3px', bgcolor: `${c.textMuted}08` }} />;
                              }
                              const color = cell.win_rate > 0.55 ? '#00A5C4' : cell.win_rate < 0.45 ? '#E4446E' : '#ECE2DB';
                              const opacity = 0.3 + 0.7 * (cell.games / maxGames);
                              return (
                                <Box
                                  key={`${dIdx}-${h}`}
                                  title={`${day} ${h}:00 — ${cell.games} games · ${Math.round(cell.win_rate * 100)}% WR`}
                                  sx={{ aspectRatio: '1', borderRadius: '3px', bgcolor: color, opacity, cursor: 'help', transition: 'transform 0.15s', '&:hover': { transform: 'scale(1.15)' } }}
                                />
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </Box>
                      {/* Legend */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2, flexWrap: 'wrap' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: '#E4446E' }} />
                          <Typography sx={{ color: c.textMuted, fontSize: '0.95rem' }}>&lt;45% WR</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: '#ECE2DB' }} />
                          <Typography sx={{ color: c.textMuted, fontSize: '0.95rem' }}>45–55%</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '3px', bgcolor: '#00A5C4' }} />
                          <Typography sx={{ color: c.textMuted, fontSize: '0.95rem' }}>&gt;55% WR</Typography>
                        </Box>
                        <Typography sx={{ color: c.textMuted, fontSize: '0.95rem', ml: { sm: 'auto' } }}>
                          Cell opacity reflects number of games played in that slot.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            );
          })()}

          {/* ── How Games End ── */}
          {termBreakdown && termBreakdown.length > 0 && (
            <Box>
              <Typography sx={{ color: c.textDark, fontSize: { xs: '1.4rem', md: '1.6rem' }, fontWeight: 800, mb: 0.5 }}>How Your Games End</Typography>
              <Typography sx={{ color: c.textMuted, fontSize: { xs: '1.05rem', md: '1.15rem' }, lineHeight: 1.7, mb: 2, maxWidth: 600 }}>
                The way your games finish reveals a lot. High checkmate rates mean you play to the end. High resignation rates (for losses) mean you know when to quit.
                High timeout rates suggest clock management issues — check the time trouble section above.
              </Typography>
              <Box sx={{ display: 'flex', gap: 0, mb: 3, borderRadius: '12px', overflow: 'hidden', border: `1px solid ${c.textMuted}20`, width: 'fit-content' }}>
                {[{ key: 'all', label: 'All Games' }, { key: 'win', label: 'How You Win' }, { key: 'lose', label: 'How You Lose' }].map(tab => (
                  <Box key={tab.key} onClick={() => setTermTab(tab.key)} sx={{ px: 2, py: 1, cursor: 'pointer', bgcolor: termTab === tab.key ? ACCENT : 'transparent', transition: 'all 0.2s' }}>
                    <Typography sx={{ color: termTab === tab.key ? '#fff' : c.textMuted, fontSize: '1.05rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{tab.label}</Typography>
                  </Box>
                ))}
              </Box>
              <Box sx={{ ...cardSx, p: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 3 }}>
                <Box sx={{ width: { xs: '100%', sm: 220 }, height: 220 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={termBreakdown} dataKey="count" nameKey="type" cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={2}>
                        {termBreakdown.map((_, i) => <Cell key={i} fill={TERM_COLORS[i % TERM_COLORS.length]} stroke="none" />)}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
                <Box sx={{ flex: 1 }}>
                  {termBreakdown.slice(0, 6).map((t, i) => (
                    <Box key={t.type} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.2 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: TERM_COLORS[i % TERM_COLORS.length], flexShrink: 0 }} />
                      <Typography sx={{ color: c.textDark, fontSize: '1.05rem', fontWeight: 600, flex: 1, textTransform: 'capitalize' }}>{t.type.replace(/_/g, ' ')}</Typography>
                      <Typography sx={{ color: c.textMuted, fontSize: '1.05rem', fontWeight: 700 }}>{t.pct}%</Typography>
                    </Box>
                  ))}
                  {termBreakdown.filter(t => t.pct > 30 && TERM_ADVICE[t.type]).map(t => (
                    <Box key={`warn-${t.type}`} sx={{ mt: 1.5, p: 1.5, borderRadius: '10px', bgcolor: '#DE734508', border: '1px solid #DE734520' }}>
                      <Typography sx={{ color: '#DE7345', fontSize: '1.05rem', fontWeight: 600, lineHeight: 1.5 }}>
                        {'\u26A0'} {t.pct}% of your games end by {t.type.replace(/_/g, ' ')} — {TERM_ADVICE[t.type]}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
